import { useEffect, type ReactNode } from 'react';
import { cn } from './cn';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  className?: string;
  /** When true, clicking the backdrop will not close the modal. */
  persistent?: boolean;
}

export function Modal({ open, onClose, title, children, className, persistent }: ModalProps) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !persistent) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose, persistent]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={() => !persistent && onClose()}
        className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
      />

      <div
        className={cn(
          'relative w-full max-w-mobile rounded-card-lg bg-white shadow-card-hover',
          'animate-in fade-in zoom-in-95',
          className,
        )}
      >
        {title && (
          <header className="flex items-center justify-between border-b border-line px-5 py-3">
            <h2 id="modal-title" className="font-display text-lg font-semibold text-ink">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-muted hover:text-ink"
              aria-label="Close modal"
            >
              ✕
            </button>
          </header>
        )}
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
